import { useOptionalSession } from './chunk-7QYYSJGW.js';
import * as React from 'react';
import { grantedEntitlements, staleClampedScopes, settingCount, ENTITLEMENT } from '@authe/fe-commons/plans';
import { resolveLoadProfile, budgetFor } from '@authe/fe-commons/budgets';
import { TX_TENANCY_SCOPES, TX_SCOPES, offeredRungs, offeredTxTenancyScopes, offeredTxScopes, ladderIsChoosable, ladderIsShared } from '@authe/fe-commons/tenancy';
import { jsx } from 'react/jsx-runtime';

var UNKNOWN = {
  plan: null,
  granted: null,
  profile: "standard",
  txScopes: TX_SCOPES,
  txTenancyScopes: TX_TENANCY_SCOPES,
  offeredRungs: null,
  // No plan read yet: no cap, like every other gate here when it cannot tell.
  settingCount: () => null
};
var PlanContext = React.createContext(UNKNOWN);
function PlanProvider({
  plan,
  overrides,
  subscriptionMetadata,
  children
}) {
  const session = useOptionalSession();
  const manager = session?.manager;
  const livePlanId = plan?.id ?? null;
  const tokenScopes = session?.session?.claims.scopes;
  const granted = React.useMemo(
    () => plan ? grantedEntitlements(plan, overrides ?? {}) : null,
    [plan, overrides]
  );
  const staleScopes = React.useMemo(
    () => staleClampedScopes(tokenScopes ?? [], granted),
    [tokenScopes, granted]
  );
  React.useEffect(() => {
    if (!manager || !livePlanId) return;
    void manager.reconcilePlan(livePlanId, void 0, staleScopes);
  }, [manager, livePlanId, staleScopes]);
  const value = React.useMemo(
    () => ({
      plan: plan ?? null,
      granted,
      profile: resolveLoadProfile(plan?.metadata ?? null, subscriptionMetadata ?? null),
      txScopes: offeredTxScopes(plan?.metadata ?? null, subscriptionMetadata ?? null),
      txTenancyScopes: offeredTxTenancyScopes(plan?.metadata ?? null, subscriptionMetadata ?? null),
      // Explicit plan/subscription menus win. Without one, a plan that cannot
      // switch workspaces should not teach the organization/customer ladder.
      // Private versus workspace sharing remains a meaningful choice.
      offeredRungs: offeredRungs(plan?.metadata ?? null, subscriptionMetadata ?? null) ?? (granted && !granted.has(ENTITLEMENT.TENANCY_SWITCHING) ? /* @__PURE__ */ new Set(["user", "tenant"]) : null),
      settingCount: (key) => settingCount(plan?.metadata ?? null, subscriptionMetadata ?? null, key)
    }),
    [plan, granted, subscriptionMetadata]
  );
  return /* @__PURE__ */ jsx(PlanContext.Provider, { value, children });
}
function usePlan() {
  return React.useContext(PlanContext);
}
function useEntitlement(entitlement) {
  const { granted } = usePlan();
  return granted === null || granted.has(entitlement);
}
function useSettingCount(key) {
  return usePlan().settingCount(key);
}
function useEntitled() {
  const { granted } = usePlan();
  return React.useCallback(
    (entitlement) => !entitlement || granted === null || granted.has(entitlement),
    [granted]
  );
}
function useLoadBudget(name) {
  const { profile } = usePlan();
  return React.useMemo(() => budgetFor(name, profile), [name, profile]);
}
function useOfferedTxScopes() {
  return usePlan().txScopes;
}
function useOfferedTxTenancyScopes() {
  return usePlan().txTenancyScopes;
}
function useOfferedRungs() {
  return usePlan().offeredRungs;
}
function useLadderChoosable() {
  return ladderIsChoosable(usePlan().offeredRungs);
}
function useLadderShared() {
  return ladderIsShared(usePlan().offeredRungs);
}

export { PlanProvider, useEntitled, useEntitlement, useLadderChoosable, useLadderShared, useLoadBudget, useOfferedRungs, useOfferedTxScopes, useOfferedTxTenancyScopes, usePlan, useSettingCount };
//# sourceMappingURL=chunk-Q76QRG57.js.map
//# sourceMappingURL=chunk-Q76QRG57.js.map